import React from "react";
import { usePageMeta } from "../lib/usePageMeta";

export const Legal = (props) => <LegalPage {...props} />;

function _clampDescription(text, min = 140, max = 160) {
  if (!text) return null;
  // Strip HTML/markdown noise
  const clean = String(text).replace(/\s+/g, " ").trim();
  if (clean.length >= min && clean.length <= max) return clean;
  if (clean.length > max) {
    // Truncate at last sentence or word boundary before 160
    const chunk = clean.slice(0, max - 1);
    const cut = Math.max(chunk.lastIndexOf(". "), chunk.lastIndexOf(", "), chunk.lastIndexOf(" "));
    return (cut > min ? chunk.slice(0, cut) : chunk).replace(/[\s,.]+$/, "") + ".";
  }
  return clean;
}

function LegalPage({ title, kind, blocks, canonicalPath, description, noindex, clarityMask }) {
  const firstBlockBody = blocks?.find((b) => b?.body)?.body || "";
  const finalDesc = description ||
    _clampDescription(firstBlockBody) ||
    `${title} — Medical Peptide Store. Research reference standards for laboratory use. Signature-required delivery, lot documentation, secure online checkout.`;
  usePageMeta({
    title,
    description: finalDesc,
    canonicalPath: canonicalPath || `/${kind}`,
    robots: noindex ? "noindex, follow" : undefined,
  });
  const maskProps = clarityMask ? { "data-clarity-mask": "true" } : {};
  return (
    <div className="max-w-3xl mx-auto px-4 py-14" data-testid={`legal-${kind}`} {...maskProps}>
      <div className="overline mb-3">Legal</div>
      <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight text-brand-navy">{title}</h1>
      <div className="mt-10 space-y-8 text-brand-text leading-relaxed text-sm">
        {blocks.map((b, i) => (
          <section key={b.heading || `block-${i}`}>
            {b.heading && <h2 className="font-display text-xl font-semibold text-brand-navy tracking-tight mb-3">{b.heading}</h2>}
            <p>{b.body}</p>
            {Array.isArray(b.externalLinks) && b.externalLinks.length > 0 && (
              <ul className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm" data-testid={`legal-links-${b.heading ? b.heading.toLowerCase().replace(/[^a-z0-9]+/g, "-") : `block-${i}`}`}>
                {b.externalLinks.map((l, li) => (
                  <li key={li}>
                    <a
                      href={l.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-accent underline underline-offset-4 hover:text-brand-navy transition-colors"
                      data-testid={l.testid}
                    >
                      {l.label} <span aria-hidden="true">↗</span>
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>
      <p className="mt-14 text-xs text-brand-muted font-mono">This page is provided for catalog purposes only and is not legal or medical advice.</p>
    </div>
  );
}

export const RegulatoryDisclaimer = () => Legal({
  kind: "regulatory-disclaimer",
  title: "Regulatory Disclaimer",
  blocks: [
    { body: "Products listed on Medical Peptide Store are distributed exclusively for laboratory or research use. They are not offered as a consumer drug, food, dietary supplement, cosmetic, or medical intervention." },
    { heading: "Not approved by any regulatory body for consumer use", body: "Product statements are provided for catalog and technical-reference purposes and are not intended to treat, cure, prevent, or diagnose any disease or condition." },
    { heading: "Eligibility varies", body: "Availability and purchasing eligibility depend on the product, customer type, intended use, and destination. Certain product categories or destinations may be restricted from time to time." },
  ],
});

export const ResearchUsePolicy = () => Legal({
  kind: "research-use-policy",
  title: "Research Use Policy",
  blocks: [
    { body: "FOR RESEARCH USE ONLY. NOT FOR HUMAN OR VETERINARY USE. Products sold by Medical Peptide Store are offered solely for lawful laboratory, analytical, educational, and research purposes. They are not intended for ingestion, injection, inhalation, nasal administration, topical use, diagnosis, treatment, mitigation, cure, or prevention of any disease or medical condition." },
    { heading: "Buyer representations", body: "By purchasing, the buyer represents that they possess the knowledge, facilities, qualifications, and legal authority necessary to handle and use the products safely and lawfully." },
    { heading: "Storage and handling", body: "Store products according to the labeled storage requirements. Do not use if the primary container appears compromised." },
    { heading: "Prohibited uses", body: "Products must not be used in humans or animals for treatment, prevention, mitigation, or diagnosis of disease. Products must not be resold to unverified parties." },
  ],
});

export const Terms = () => Legal({
  kind: "terms",
  title: "Terms & Conditions",
  description: "Terms and conditions for the Medical Peptide Store catalog and services. Account eligibility, pricing, payment, and compliance provisions apply to every order.",
  blocks: [
    { body: "These Terms govern access to and use of the Medical Peptide Store catalog and services." },
    { heading: "Account eligibility", body: "You must provide accurate information during account creation. We reserve the right to suspend accounts that provide misleading information." },
    { heading: "Pricing", body: "Prices displayed are for reference and are subject to change. Final pricing is calculated server-side at checkout." },
    { heading: "Payment", body: "Payment processing is provided by a third-party processor. Availability of live checkout depends on the commerce mode and per-product compliance status." },
  ],
});

export const Privacy = () => Legal({
  kind: "privacy",
  title: "Privacy Policy",
  blocks: [
    { body: "We collect the minimum information necessary to operate the catalog and verify accounts. Verification documents, license numbers, and tax identifiers are stored securely and are not sent to advertising platforms." },
    { heading: "Cookies", body: "We use essential cookies for authentication and cart persistence, and analytics cookies only after consent where required by law." },
    { heading: "Analytics we use", body: "With your consent we use Google Analytics 4 (via Google Tag Manager) to measure aggregate traffic and shop performance, Google Ads to measure the effectiveness of ads that brought you here, and Microsoft Clarity to record anonymous heatmaps and session recordings of how visitors interact with the catalog. Clarity helps us fix confusing or broken parts of the shop; it never records your account details, order history, cart contents, checkout, shipping address, payment page, admin pages, order-tracking, or any auth or newsletter form — those areas are fully masked before recording. We do not send Clarity your name, email, address, order id, session id, search terms, or any other personal information." },
    { heading: "Your consent choices", body: "The cookie banner offers Accept analytics or Essential only. Accept analytics enables Google Analytics 4 and Microsoft Clarity for anonymous, aggregate research; Essential only keeps analytics off. You can change your mind at any time — clear the mps_cookie_consent_v1 entry in your browser storage or use the banner if it reappears; the change takes effect immediately without a reload. Advertising cookies stay off by default because we do not surface a separate marketing consent option today." },
    { heading: "Third-party privacy statements", body: "Microsoft Clarity is a Microsoft product; you can read Microsoft's privacy statement, which covers Clarity, at the link below. Google Analytics and Google Ads privacy is described by Google at the link below. This section describes how we use those tools; it is not a statement of legal compliance with any specific privacy regime.",
      externalLinks: [
        { label: "Microsoft Privacy Statement", href: "https://privacy.microsoft.com/en-us/privacystatement", testid: "privacy-microsoft-link" },
        { label: "Google Privacy & Terms",       href: "https://policies.google.com/privacy",                testid: "privacy-google-link" },
      ] },
    { heading: "Requests", body: "Users may request access, correction, or deletion of personal data by contacting info@medicalpeptidestore.com." },
  ],
});

export const ShippingPolicy = () => Legal({
  kind: "shipping-policy",
  title: "Shipping Policy",
  blocks: [
    { heading: "Shipping calculated at checkout", body: "Shipping is quoted live at checkout using our carrier partners. During checkout you'll enter your destination address and see every eligible service, carrier, and price before confirming payment. Every physical order ships signature-required. Peptide reference standards ship as room-temperature lyophilized powder in tamper-evident cartons. Delivery timeframes are estimates only and are not guaranteed; actual delivery times may vary based on destination, carrier delays, weather, holidays, or other circumstances outside the seller's control." },
    
    { heading: "Signature required at delivery", body: "Every shipment requires a recipient signature at delivery. Packages will not be left unattended or released without the carrier obtaining the required signature. The customer is responsible for providing a complete and accurate shipping address, ensuring an authorized recipient is available to sign, monitoring tracking information, and arranging redelivery or carrier pickup after a missed delivery attempt." },
    { heading: "Room-temperature transit", body: "All peptide reference standards are lyophilized (freeze-dried) powders that ship at room temperature. Post-delivery storage instructions on the product page are unaffected by transit — recipients should follow lot-specific storage guidance once the parcel arrives." },
  ],
});

export const ReturnPolicy = () => Legal({
  kind: "return-policy",
  canonicalPath: "/returns",
  title: "Final-Sale & Claims Policy",
  blocks: [
    { heading: "All sales are final", body: "Due to the nature of the products sold, storage requirements, chain-of-custody considerations, and the inability to verify how products have been handled after delivery, all sales are final. Medical Peptide Store does not accept returns, refunds, exchanges, cancellations after shipment, replacements based on customer preference, or replacements resulting from improper storage or handling after delivery." },
    { heading: "Limited claims process", body: "Except where required by applicable law, no refunds, returns, exchanges, or replacements will be provided after shipment. This policy does not limit any rights that cannot legally be waived or claims involving an incorrect item, verified fulfillment error, carrier-confirmed loss, documented shipping damage, or a shipment that arrives outside the estimated delivery window because of a documented carrier delay." },
    { heading: "How to submit a claim (within 24 hours of delivery)", body: "Send an email to info@medicalpeptidestore.com with your order number, photographs of the product, photographs of the shipping box, photographs of the label and packing materials, a description of the issue, and carrier tracking information. Retain all products and packaging until the claim has been reviewed." },
    { heading: "Refunds when approved", body: "Approved refunds are processed to the original payment method within 5–10 business days." },
  ],
});

export const WarrantyDisclaimer = () => Legal({
  kind: "warranty-disclaimer",
  canonicalPath: "/warranty",
  title: "Disclaimer of Warranties",
  description: "Disclaimer of warranties for Medical Peptide Store. Products sold as-is without express or implied warranty, to the fullest extent permitted by applicable law.",
  blocks: [
    { body: "To the fullest extent permitted by applicable law, all products are sold \"as is\" and \"as available,\" without any express warranty, guarantee, representation, or promise concerning results, performance, fitness for a particular purpose, merchantability, compatibility, effectiveness, stability after delivery, or suitability for any intended application." },
    { heading: "No outcome guarantee", body: "Medical Peptide Store does not warrant or guarantee any particular research outcome or result. Product information is provided for general identification and informational purposes only and does not constitute medical, scientific, legal, or professional advice." },
    { heading: "Applicable-law qualification", body: "Some jurisdictions do not permit the exclusion of certain implied warranties. Therefore, these exclusions apply only to the maximum extent permitted by applicable law." },
  ],
});

export const AcceptableUse = () => Legal({
  kind: "acceptable-use",
  title: "Acceptable Use Policy",
  description: "Acceptable use policy for Medical Peptide Store: users must not bypass compliance controls, misrepresent intended use, or resell products to unverified parties.",
  blocks: [
    { body: "Users must not attempt to bypass compliance controls, misrepresent their intended use, or use the platform for unlawful purposes." },
    { heading: "Prohibited activities", body: "Reselling products to unverified parties, submitting false verification documentation, and using automated tools to access non-public documentation are prohibited." },
  ],
});

export const Accessibility = () => Legal({
  kind: "accessibility",
  title: "Accessibility Statement",
  description: "Accessibility statement for Medical Peptide Store — we aim to conform to WCAG 2.1 AA standards. Report accessibility issues to us for resolution.",
  blocks: [
    { body: "We aim to conform to WCAG 2.1 AA standards. If you experience an accessibility barrier, contact us and we will work to resolve it." },
  ],
});

export const CookiePolicy = () => Legal({
  kind: "cookie-policy",
  title: "Cookie Policy",
  description: "Cookie policy for Medical Peptide Store. Essential cookies maintain login and cart. Non-essential analytics cookies enabled only after consent where required.",
  blocks: [
    { body: "Essential cookies are used to maintain your login session and cart. Non-essential analytics or advertising cookies are only enabled after consent where required." },
    { heading: "What we set with Accept analytics", body: "Google Analytics 4 (measurement id G-KVNPTG545S, loaded directly by the site — this site does not use a Google Tag Manager container) sets first-party analytics cookies to measure how the catalog is used. Microsoft Clarity (project ya2b98n3bu) records anonymous heatmaps and session recordings; it uses first-party storage that starts denied and is only turned on after you pick Accept analytics." },
    { heading: "What Clarity does not see", body: "Checkout, payment, account, admin, order tracking, and every auth or newsletter form are marked to be fully masked from Clarity recordings before any pixel is captured. We also never send Clarity your name, email, order id, session id, search terms, or cart contents — only a short, non-personal event name like view_item or purchase for aggregate funnel analysis." },
    { heading: "How to change your mind", body: "Selecting Essential only sends explicit deny signals to both Google (analytics_storage, ad_storage, ad_user_data, ad_personalization) and Microsoft (analytics_Storage, ad_Storage). The change takes effect immediately — you do not need to reload the page. This policy describes our current practice; it is not a statement of legal compliance with any specific privacy regime.",
      externalLinks: [
        { label: "Microsoft Privacy Statement (covers Clarity)", href: "https://privacy.microsoft.com/en-us/privacystatement", testid: "cookie-microsoft-link" },
        { label: "Google Privacy & Terms",                        href: "https://policies.google.com/privacy",                testid: "cookie-google-link" },
      ] },
  ],
});

export const StateRestrictions = () => Legal({
  kind: "state-restrictions",
  title: "State Restrictions",
  description: "State restrictions for research peptide reference standards — certain compounds may not ship to specific U.S. states. Compliance-checked at checkout.",
  blocks: [
    { body: "Certain research compounds may not ship to specific U.S. states or jurisdictions. The compliance engine evaluates every cart before checkout to enforce destination-based restrictions." },
  ],
});

export const RecallNotices = () => Legal({
  kind: "recall-notices",
  title: "Product Recall & Safety Notices",
  description: "Product recall and safety notices for Medical Peptide Store. No active recalls at this time. If issued, notices post here and email affected account holders.",
  blocks: [
    { body: "No active recalls at this time. Recall notices, if issued, will be posted on this page and emailed to affected account holders." },
  ],
});

export const OrderTracking = () => Legal({
  kind: "order-tracking",
  title: "Order Tracking",
  noindex: true,
  clarityMask: true,   // order + shipment PII — mask from heatmaps
  blocks: [
    { body: "Account holders can view live shipment status inside their account dashboard once orders are dispatched. Products shown as in stock in the catalog may be purchased through secure online checkout; availability, shipping eligibility, and order acceptance remain subject to applicable product and destination restrictions." },
    { heading: "Where to find tracking", body: "After you place an order, sign in at /account to see the current shipment status, carrier, and tracking number. A tracking link is also emailed as soon as your parcel is dispatched." },
  ],
});

export const Sitemap = () => Legal({
  kind: "sitemap",
  title: "Sitemap",
  blocks: [
    { body: "See the footer for a full list of catalog, account, support, and legal pages." },
  ],
});

export const AccountApplicationReceived = () => Legal({
  kind: "application-received",
  title: "Application Received",
  noindex: true,
  blocks: [
    { body: "Thank you. Your application has been received and assigned to a verification specialist. We will be in touch with any follow-up questions." },
    { heading: "What happens next?", body: "A staff reviewer will validate the documentation you provided. Expect a decision within 1–3 business days for most applications." },
  ],
});
